import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Card, CardMedia, Chip, Typography } from '@mui/material'
import AlumnoHeader from '../../../components/AlumnoHeader/AlumnoHeader'
import PageLoading from '../../../components/PageLoading/PageLoading'
import {
  getAlumno,
  updateFacturacionAlumno,
  updateFichaAlumno,
  updateTelefonosAlumno,
  updateEmailsAlumno
} from '../../../utils/api/alumnos'
import { useNotification } from '../../../context/NotificationManager/NotificationManager'
import {
  dateFromString,
  dateFromUnixTimestamp,
  dateToString,
  dateToStringLong,
  forwardDays,
  getDateFromObjectId
} from '../../../utils/date'
import { getPerfilAcademico } from '../../../utils/model/alumnos'
import useAlumno from '../../../hooks/useAlumno'
import { getAuth } from '../../../utils/auth'
import DatosPersonales from './components/DatosPersonales/DatosPersonales'
import DatosConnect from './components/DatosConnect/DatosConnect'
import ModalEditarFichaAlumno from '../../../modals/ModalEditarFichaAlumno/ModalEditarFichaAlumno'
import DatosFacturacion from './components/DatosFacturacion/DatosFacturacion'
import ProgresoFicha from '../../../components/ProgresoFicha/ProgresoFicha'
import ModalEditarFacturacionAlumno from '../../../modals/ModalEditarFacturacionAlumno/ModalEditarFacturacionAlumno'
import user from './profile.jpg'
import DatosLegal from './components/DatosLegal/DatosLegal'
import { downloadCloudDocument } from '../../../utils/documents'
import { getPacksAlumno } from '../../../utils/api/packs'
import ModalPacksMesFicha from '../../../modals/ModalPacksMesFicha/ModalPacksMesFicha'
import DatosTelefono from './components/DatosTelefono/DatosTelefono'
import DatosEmail from './components/DatosEmail/DatosEmail'
import ModalEditarDatosTelefono from '../../../modals/ModalEditarDatosTelefono/ModalEditarDatosTelefono'
import ModalEditarDatosEmail from '../../../modals/ModalEditarDatosEmail/ModalEditarDatosEmail'
import css from './Ficha.module.css'

const Ficha = () => {
  const userData = getAuth()
  const notification = useNotification()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { id, nombre } = useAlumno()

  const [isEditarDatosTelefonoOpen, setEditarDatosTelefonoOpen] =
    useState(false)
  const [isEditarDatosEmailOpen, setEditarDatosEmailOpen] = useState(false)
  const [isEditModalOpen, setEditModalOpen] = useState(false)
  const [isFacturacionModalOpen, setFacturacionModalOpen] = useState(false)
  const [mostrarPacks, setMostrarPacks] = useState(false)

  const { isLoading, data: fichaAlumno = {} } = useQuery({
    queryKey: ['alumnos', id],
    queryFn: () =>
      getAlumno(id)
        .then(datos => ({
          ...datos,
          fechaNacimiento: dateFromUnixTimestamp(datos.fechaNacimiento),
          fechaFinMatricula: dateFromUnixTimestamp(datos.fechaFinMatricula),
          perfilAcademico: getPerfilAcademico({
            curso: datos.cursoEstudios,
            nivel: datos.nivelEstudios,
            centroEstudios: datos.centroEstudios
          })
        }))
        .catch(err => {
          notification.error({
            title: 'Error obteniendo datos de alumno',
            content: err
          })
          navigate('/alumnos')
          return {}
        })
  })

  const fotoUrl = fichaAlumno.fotoUrl || ''

  const { data: alumnoPacks = [] } = useQuery({
    queryKey: ['packs-alumnos', id, 'recientes'],
    queryFn: () =>
      getPacksAlumno(id)
        .then(datos =>
          datos
            .map(pack => ({
              ...pack,
              fecha: dateToString(getDateFromObjectId(pack.id)),
              fechaCaducidad: dateToString(pack.fechaCaducidad)
            }))
            .filter(
              pack =>
                dateFromString(pack.fechaCaducidad) >
                forwardDays(new Date(), -50)
            )
        )
        .catch(err => {
          notification.error({ title: 'Error obteniendo packs', content: err })
          return []
        })
  })

  const { isPending: isAlumnoUpdating, mutate: updateAlumno } = useMutation({
    mutationFn: updateFichaAlumno,
    onSuccess: () => {
      notification.success({
        title: 'Alumno actualizado',
        content: 'Datos modificados correctamente'
      })
      queryClient.invalidateQueries({ queryKey: ['alumnos', id] })
      setEditModalOpen(false)
    },
    onError: err => {
      notification.error({
        title: 'Error actualizando el alumno',
        content: err
      })
    }
  })

  const { isPending: isFacturacionUpdating, mutate: updateFacturacion } =
    useMutation({
      mutationFn: updateFacturacionAlumno,
      onSuccess: () => {
        notification.success({
          title: 'Alumno actualizado',
          content: 'Datos de facturación actualizados'
        })
        queryClient.invalidateQueries({ queryKey: ['alumnos', id] })
        setFacturacionModalOpen(false)
      },
      onError: err => {
        notification.error({
          title: 'Error actualizando facturación',
          content: err
        })
      }
    })

  const { isPending: isTelefonosUpdating, mutate: actualizarTelefonos } =
    useMutation({
      mutationFn: updateTelefonosAlumno,
      onSuccess: () => {
        notification.success({
          title: 'Alumno actualizado',
          content: 'Teléfonos modificados correctamente'
        })
        queryClient.invalidateQueries({ queryKey: ['alumnos', id] })
        setEditarDatosTelefonoOpen(false)
      },
      onError: err => {
        notification.error({
          title: 'Error actualizando los teléfonos',
          content: err
        })
      }
    })

  const { isPending: isEmailsUpdating, mutate: actualizarEmails } = useMutation(
    {
      mutationFn: updateEmailsAlumno,
      onSuccess: () => {
        notification.success({
          title: 'Alumno actualizado',
          content: 'Emails modificados correctamente'
        })
        queryClient.invalidateQueries({ queryKey: ['alumnos', id] })
        setEditarDatosEmailOpen(false)
      },
      onError: err => {
        notification.error({
          title: 'Error actualizando los emails',
          content: err
        })
      }
    }
  )

  const handleUpdateFichaAlumno = data => {
    if (isAlumnoUpdating) return
    updateAlumno(data)
  }

  const handleUpdateFacturacionAlumno = data => {
    if (isFacturacionUpdating) return
    updateFacturacion({ id, ...data })
  }

  const handleDownloadCondiciones = () => {
    if (!hasCondiciones) return
    downloadCloudDocument(fichaAlumno.condicionesUrl, `condiciones-${id}.pdf`)
  }

  const handleDownloadRGPD = () => {
    if (!hasRGPD) return
    downloadCloudDocument(fichaAlumno.rgpdUrl, `rgpd-${id}.pdf`)
  }

  const handleUpdateTelefonos = telefonos => {
    if (isTelefonosUpdating) return
    actualizarTelefonos({ id, telefonos })
  }

  const handleUpdateEmails = emails => {
    if (isEmailsUpdating) return
    actualizarEmails({ id, emails })
  }

  const hasCondiciones = !!fichaAlumno.condicionesUrl
  const hasRGPD = !!fichaAlumno.rgpdUrl

  return (
    <PageLoading isLoading={isLoading}>
      <div className={css.main}>
        <AlumnoHeader label='Ficha' />
        <div className={css.alumno}>
          <Card variant='outlined' className={css.summary}>
            <CardMedia
              className={css.image}
              component='img'
              image={fotoUrl || user}
              alt={nombre}
            />
            <div className={css.content}>
              <Typography variant='h6' component='h2'>
                {nombre}
              </Typography>
              <Typography variant='body2' component='p'>
                {fichaAlumno.perfilAcademico}
              </Typography>
              <section className={css.section} data-section='matricula'>
                <Typography variant='body2' component='p'>
                  Caducidad de la matrícula:{' '}
                  <strong>
                    {dateToStringLong(fichaAlumno.fechaFinMatricula)}
                  </strong>
                </Typography>
              </section>
              <section className={css.section} data-section='lead'>
                {fichaAlumno.leadId ? (
                  <Chip
                    className={css.chip}
                    icon={<i className='material-icons'>face</i>}
                    component={Link}
                    to={`/leads/${fichaAlumno.leadId}/ficha`}
                    label='Ver ficha lead'
                    variant='outlined'
                  />
                ) : (
                  <Typography variant='body2' component='p'>
                    No hay lead asociado a este alumno
                  </Typography>
                )}
              </section>
              <section data-section='resumen'>
                {alumnoPacks.length > 0 && (
                  <Chip
                    className={css.chip}
                    icon={<i className='material-icons'>library_books</i>}
                    label='Packs recientes'
                    color='success'
                    variant='outlined'
                    onClick={() => setMostrarPacks(true)}
                  />
                )}
              </section>
              <section className={css.section} data-section='documentacion'>
                <ProgresoFicha className={css.progreso} alumno={fichaAlumno} />
                <Chip
                  className={css.chip}
                  icon={
                    <i className='material-icons'>
                      {hasCondiciones ? 'check' : 'close'}
                    </i>
                  }
                  color={hasCondiciones ? 'primary' : 'error'}
                  onClick={handleDownloadCondiciones}
                  href={fichaAlumno.condicionesUrl}
                  label='Condiciones contratacion'
                  variant='outlined'
                />
                <Chip
                  className={css.chip}
                  icon={
                    <i className='material-icons'>
                      {hasRGPD ? 'check' : 'close'}
                    </i>
                  }
                  color={hasRGPD ? 'primary' : 'error'}
                  onClick={handleDownloadRGPD}
                  label='Aceptación RGPD'
                  variant='outlined'
                />
              </section>
              {userData.centro !== fichaAlumno.centro && (
                <section className={css.section} data-section='ficha'>
                  <Typography
                    className={css.academy}
                    variant='body2'
                    component='p'
                  >
                    <i className='material-icons'>school</i>
                    Alumno de <strong>{fichaAlumno.centro}</strong>
                  </Typography>
                </section>
              )}
            </div>
          </Card>
          <div className={css.ficha}>
            <div className={css.accordion}>
              <DatosPersonales
                alumno={fichaAlumno}
                onEdit={() => setEditModalOpen(true)}
              />
            </div>
            <div className={css.accordion}>
              <DatosTelefono
                alumno={fichaAlumno}
                onEdit={() => setEditarDatosTelefonoOpen(true)}
              />
            </div>
            <div className={css.accordion}>
              <DatosEmail
                alumno={fichaAlumno}
                onEdit={() => setEditarDatosEmailOpen(true)}
              />
            </div>
            <div className={css.accordion}>
              <DatosFacturacion
                facturacion={fichaAlumno.facturacion}
                onEdit={() => setFacturacionModalOpen(true)}
              />
            </div>
            <div className={css.accordion}>
              <DatosConnect alumno={fichaAlumno} />
            </div>
            <div className={css.accordion}>
              <DatosLegal alumno={fichaAlumno} />
            </div>
          </div>
          <ModalEditarFichaAlumno
            open={isEditModalOpen}
            alumno={fichaAlumno}
            onSubmit={handleUpdateFichaAlumno}
            onClose={() => setEditModalOpen(false)}
          />
          <ModalEditarDatosTelefono
            open={isEditarDatosTelefonoOpen}
            telefonosContacto={fichaAlumno.telefonos}
            onClose={() => setEditarDatosTelefonoOpen(false)}
            onSubmit={handleUpdateTelefonos}
          />
          <ModalEditarDatosEmail
            open={isEditarDatosEmailOpen}
            emailsContacto={fichaAlumno.emails}
            onClose={() => setEditarDatosEmailOpen(false)}
            onSubmit={handleUpdateEmails}
          />
          <ModalEditarFacturacionAlumno
            open={isFacturacionModalOpen}
            facturacion={fichaAlumno.facturacion}
            onSubmit={handleUpdateFacturacionAlumno}
            onClose={() => setFacturacionModalOpen(false)}
          />
          <ModalPacksMesFicha
            packs={alumnoPacks}
            open={mostrarPacks}
            onClose={() => setMostrarPacks(false)}
          />
        </div>
      </div>
    </PageLoading>
  )
}

export default Ficha
